/**
 * Shape of validation errors returned by the API (e.g. from Zod).
 */
interface ApiValidationError {
  path?: (string | number)[]
  message?: string
}

/**
 * Shape of error response body from the API (e.g. from GlobalExceptionFilter).
 */
export interface ApiErrorResponse {
  message?: string
  error?: string
  errors?: ApiValidationError[]
}

/**
 * Formats an API error into a user-friendly message.
 * Uses validation errors when present (e.g. "Group name is required", "configurationData.headquarters.email: Invalid email address"),
 * otherwise falls back to the response message or a generic message.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  const data = getApiErrorResponse(error)
  if (!data) {
    if (error instanceof Error && error.message) return error.message
    return fallback
  }

  const validationErrors = data.errors
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    const lines = validationErrors.map(e => {
      const path = (e.path ?? []).filter(Boolean).join('.')
      const msg = e.message ?? 'Invalid value'
      return path ? `${path}: ${msg}` : msg
    })
    return lines.join('\n')
  }

  if (data.message && data.message !== 'Validation failed') {
    return data.message
  }

  if (data.message) {
    return data.message
  }

  if (error instanceof Error && error.message) return error.message
  return fallback
}

/**
 * Returns the raw API error response body if present (e.g. from an axios error).
 */
export function getApiErrorResponse(error: unknown): ApiErrorResponse | null {
  if (!error || typeof error !== 'object') return null
  const err = error as { response?: { data?: ApiErrorResponse } }
  if (err.response?.data && typeof err.response.data === 'object') {
    return err.response.data as ApiErrorResponse
  }
  return null
}
